import React, { useState, useEffect } from 'react';
import { RefreshCw, Search, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INSTRUMENT_TYPES = ['Equity', 'ETF', 'Equity Mutual Fund', 'Debt Mutual Fund', 'Bond', 'FD', 'Other'];
const isEquityType = (t) => t === 'Equity' || t === 'ETF';
const isMFType = (t) => t === 'Equity Mutual Fund' || t === 'Debt Mutual Fund';

const emptyForm = (pid) => ({
  portfolioId: pid || '',
  instrumentType: 'Equity',
  schemeCode: '',
  ticker: '',
  shareName: '',
  exchange: 'NSE',
  quantity: '',
  purchasePrice: '',
  purchaseDate: '',
  currentMarketPrice: '',
  sellPrice: '',
  sellDate: '',
  quantitySold: '',
  status: 'held',
  notes: '',
});

const AddTransactionModal = ({ isOpen, onClose, onSubmit, editingTransaction, portfolios = [], defaultPortfolioId = '' }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState(emptyForm(defaultPortfolioId));
  const [fetchingPrice, setFetchingPrice] = useState(false);

  // Mutual fund search
  const [mfQuery, setMfQuery] = useState('');
  const [mfResults, setMfResults] = useState([]);
  const [mfSearching, setMfSearching] = useState(false);

  const itype = formData.instrumentType;
  const isEquity = isEquityType(itype);
  const isMF = isMFType(itype);
  const isManual = !isEquity && !isMF;

  useEffect(() => {
    if (editingTransaction) {
      setFormData({ ...emptyForm(defaultPortfolioId), ...editingTransaction, portfolioId: editingTransaction.portfolioId || defaultPortfolioId, instrumentType: editingTransaction.instrumentType || 'Equity' });
      setMfQuery(editingTransaction.shareName || '');
    } else {
      setFormData(emptyForm(defaultPortfolioId));
      setMfQuery('');
    }
    setMfResults([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTransaction, isOpen, defaultPortfolioId]);

  // Debounced MF search
  useEffect(() => {
    if (!isMF) return;
    if (mfQuery.trim().length < 3) { setMfResults([]); return; }
    const t = setTimeout(async () => {
      setMfSearching(true);
      try {
        const res = await axios.get(`${API}/mf/search`, { params: { q: mfQuery.trim() } });
        setMfResults(res.data);
      } catch (e) {
        setMfResults([]);
      } finally {
        setMfSearching(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [mfQuery, isMF]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleSelectScheme = (scheme) => {
    setFormData((prev) => ({ ...prev, schemeCode: scheme.schemeCode, shareName: scheme.schemeName, ticker: scheme.schemeCode }));
    setMfQuery(scheme.schemeName);
    setMfResults([]);
  };

  const handleFetchLivePrice = async () => {
    if (!formData.ticker.trim()) {
      toast({ title: 'Enter a ticker', description: 'Please enter a ticker symbol first.', variant: 'destructive' });
      return;
    }
    try {
      setFetchingPrice(true);
      const res = await axios.get(`${API}/quote/${encodeURIComponent(formData.ticker.trim())}?exchange=${formData.exchange}`);
      setField('currentMarketPrice', String(res.data.price));
      toast({ title: 'Live Price Fetched', description: `${formData.ticker.toUpperCase()} (${formData.exchange}): ₹${res.data.price}` });
    } catch (error) {
      toast({ title: 'Could not fetch price', description: error.response?.data?.detail || 'Check the ticker symbol and exchange.', variant: 'destructive' });
    } finally {
      setFetchingPrice(false);
    }
  };

  const handleFetchNav = async () => {
    if (!formData.schemeCode) {
      toast({ title: 'Select a fund', description: 'Search and select a mutual fund first.', variant: 'destructive' });
      return;
    }
    try {
      setFetchingPrice(true);
      const res = await axios.get(`${API}/mf/nav/${encodeURIComponent(formData.schemeCode)}`);
      setField('currentMarketPrice', String(res.data.nav));
      toast({ title: 'Latest NAV Fetched', description: `${formData.shareName}: ₹${res.data.nav}` });
    } catch (error) {
      toast({ title: 'Could not fetch NAV', description: error.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setFetchingPrice(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.portfolioId) {
      toast({ title: 'Select a portfolio', description: 'Please choose which portfolio this belongs to.', variant: 'destructive' });
      return;
    }
    let exchange = formData.exchange;
    if (isMF) exchange = 'MF';
    else if (isManual) exchange = formData.exchange && formData.exchange !== 'NSE' && formData.exchange !== 'BSE' ? formData.exchange : 'NA';
    let ticker = formData.ticker;
    if (isMF) ticker = formData.schemeCode || formData.ticker;
    if (!ticker) ticker = (formData.shareName || '').toUpperCase().slice(0, 20);

    const submissionData = {
      portfolioId: formData.portfolioId,
      instrumentType: formData.instrumentType,
      schemeCode: isMF ? formData.schemeCode || null : null,
      ticker,
      shareName: formData.shareName,
      exchange,
      quantity: parseFloat(formData.quantity),
      purchasePrice: parseFloat(formData.purchasePrice),
      purchaseDate: formData.purchaseDate,
      currentMarketPrice: formData.currentMarketPrice ? parseFloat(formData.currentMarketPrice) : parseFloat(formData.purchasePrice),
      sellPrice: formData.sellPrice ? parseFloat(formData.sellPrice) : null,
      sellDate: formData.sellDate || null,
      quantitySold: formData.quantitySold ? parseFloat(formData.quantitySold) : parseFloat(formData.quantity),
      status: formData.status,
      notes: formData.notes || null,
    };
    onSubmit(submissionData);
  };

  const qtyLabel = isMF ? 'Units *' : 'Quantity *';
  const buyLabel = isMF ? 'Purchase NAV (₹) *' : 'Purchase Price (₹) *';
  const curLabel = isMF ? 'Current NAV (₹)' : 'Current Price (₹)';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900">
            {editingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700">Portfolio *</Label>
              <Select value={formData.portfolioId} onValueChange={(v) => setField('portfolioId', v)}>
                <SelectTrigger className="border-slate-300 focus:border-orange-500" data-testid="txn-portfolio-select">
                  <SelectValue placeholder="Select portfolio" />
                </SelectTrigger>
                <SelectContent>
                  {portfolios.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Instrument Type *</Label>
              <Select value={formData.instrumentType} onValueChange={(v) => setField('instrumentType', v)}>
                <SelectTrigger className="border-slate-300 focus:border-orange-500" data-testid="txn-instrument-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSTRUMENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Mutual Fund search */}
          {isMF && (
            <div className="space-y-2 relative">
              <Label className="text-slate-700">Search Mutual Fund (by name) *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={mfQuery}
                  onChange={(e) => setMfQuery(e.target.value)}
                  placeholder="e.g., Axis Bluechip, SBI, HDFC Flexi"
                  className="pl-10 border-slate-300 focus:border-orange-500"
                  data-testid="mf-search-input"
                />
                {mfSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
              </div>
              {mfResults.length > 0 && (
                <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-xl max-h-56 overflow-y-auto" data-testid="mf-search-results">
                  {mfResults.map((s) => (
                    <button
                      type="button"
                      key={s.schemeCode}
                      onClick={() => handleSelectScheme(s)}
                      className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm border-b border-slate-100 last:border-0"
                    >
                      <div className="font-medium text-slate-800">{s.schemeName}</div>
                      <div className="text-xs text-slate-500">Scheme code: {s.schemeCode}</div>
                    </button>
                  ))}
                </div>
              )}
              {formData.schemeCode && (
                <p className="text-xs text-emerald-600">Selected: {formData.shareName} (code {formData.schemeCode})</p>
              )}
            </div>
          )}

          {/* Equity / ETF fields */}
          {isEquity && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ticker" className="text-slate-700">Ticker Symbol *</Label>
                <Input id="ticker" name="ticker" value={formData.ticker} onChange={handleChange} placeholder="e.g., RELIANCE" required className="border-slate-300 focus:border-orange-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Exchange *</Label>
                <Select value={formData.exchange} onValueChange={(v) => setField('exchange', v)}>
                  <SelectTrigger className="border-slate-300 focus:border-orange-500"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NSE">NSE</SelectItem>
                    <SelectItem value="BSE">BSE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Name (for equity & manual; MF name is auto from search) */}
          {!isMF && (
            <div className="space-y-2">
              <Label htmlFor="shareName" className="text-slate-700">{isManual ? 'Instrument Name *' : 'Share Name *'}</Label>
              <Input id="shareName" name="shareName" value={formData.shareName} onChange={handleChange} placeholder={isManual ? 'e.g., 7.26% GOI 2033 Bond' : 'e.g., Reliance Industries Ltd.'} required className="border-slate-300 focus:border-orange-500" />
            </div>
          )}

          {isManual && (
            <div className="space-y-2">
              <Label htmlFor="ticker" className="text-slate-700">Code / ISIN (optional)</Label>
              <Input id="ticker" name="ticker" value={formData.ticker} onChange={handleChange} placeholder="e.g., INE002A01018 (optional)" className="border-slate-300 focus:border-orange-500" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity" className="text-slate-700">{qtyLabel}</Label>
              <Input id="quantity" name="quantity" type="number" step="any" min="0" value={formData.quantity} onChange={handleChange} placeholder={isMF ? 'No. of units' : 'No. of shares'} required className="border-slate-300 focus:border-orange-500" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="purchasePrice" className="text-slate-700">{buyLabel}</Label>
              <Input id="purchasePrice" name="purchasePrice" type="number" step="0.01" min="0" value={formData.purchasePrice} onChange={handleChange} placeholder="Price per unit" required className="border-slate-300 focus:border-orange-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="purchaseDate" className="text-slate-700">Purchase Date *</Label>
              <Input id="purchaseDate" name="purchaseDate" type="date" value={formData.purchaseDate} onChange={handleChange} required className="border-slate-300 focus:border-orange-500" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currentMarketPrice" className="text-slate-700">{curLabel}</Label>
              <div className="flex gap-2">
                <Input id="currentMarketPrice" name="currentMarketPrice" type="number" step="0.01" min="0" value={formData.currentMarketPrice} onChange={handleChange} placeholder={isManual ? 'Enter manually' : 'Latest value'} className="border-slate-300 focus:border-orange-500" />
                {isEquity && (
                  <Button type="button" variant="outline" onClick={handleFetchLivePrice} disabled={fetchingPrice} title="Fetch live price from NSE/BSE" className="border-blue-600 text-blue-600 hover:bg-blue-50 shrink-0 px-3" data-testid="fetch-live-price-btn">
                    <RefreshCw className={`h-4 w-4 ${fetchingPrice ? 'animate-spin' : ''}`} />
                  </Button>
                )}
                {isMF && (
                  <Button type="button" variant="outline" onClick={handleFetchNav} disabled={fetchingPrice} title="Fetch latest NAV (AMFI)" className="border-blue-600 text-blue-600 hover:bg-blue-50 shrink-0 px-3" data-testid="fetch-nav-btn">
                    <RefreshCw className={`h-4 w-4 ${fetchingPrice ? 'animate-spin' : ''}`} />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700">Status *</Label>
            <Select value={formData.status} onValueChange={(v) => setField('status', v)}>
              <SelectTrigger className="border-slate-300 focus:border-orange-500"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="held">Held</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.status === 'sold' && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-700">Sale Information</h4>
              {editingTransaction && (
                <div className="space-y-2">
                  <Label htmlFor="quantitySold" className="text-slate-700">{isMF ? 'Units Sold' : 'Quantity Sold'} (of {(parseFloat(formData.quantity) || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })})</Label>
                  <Input
                    id="quantitySold"
                    name="quantitySold"
                    type="number"
                    step="any"
                    min="0"
                    max={formData.quantity}
                    value={formData.quantitySold || ''}
                    onChange={handleChange}
                    placeholder={`Leave blank to sell all ${formData.quantity || ''}`}
                    className="border-slate-300 focus:border-orange-500"
                    data-testid="txn-quantity-sold-input"
                  />
                  <p className="text-xs text-slate-500">Partial sell splits this into a sold lot + a remaining held lot (keeps Capital Gains accurate).</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sellPrice" className="text-slate-700">{isMF ? 'Sell NAV (₹)' : 'Sell Price (₹)'}</Label>
                  <Input id="sellPrice" name="sellPrice" type="number" step="0.01" min="0" value={formData.sellPrice || ''} onChange={handleChange} placeholder="Price per unit" className="border-slate-300 focus:border-orange-500" data-testid="txn-sell-price-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sellDate" className="text-slate-700">Sell Date</Label>
                  <Input id="sellDate" name="sellDate" type="date" value={formData.sellDate || ''} onChange={handleChange} className="border-slate-300 focus:border-orange-500" data-testid="txn-sell-date-input" />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-slate-700">Comments / Notes</Label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
              rows={2}
              placeholder="Optional: e.g. bonus shares, SIP installment, rationale, broker note…"
              className="w-full rounded-md border border-slate-300 focus:border-orange-500 focus:outline-none px-3 py-2 text-sm resize-y"
              data-testid="txn-notes-input"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-300 hover:bg-slate-100">Cancel</Button>
            <Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="txn-submit-btn">{editingTransaction ? 'Update' : 'Add'} Transaction</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTransactionModal;
